import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2, Medal } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const ROLES = ["Owner / Executive", "Regional Manager", "Community Manager", "Maintenance Lead", "Other"];
const PORTFOLIO_SIZES = ["", "1–250 units / pads", "251–500 units / pads", "501–1,500 units / pads", "1,500+ units / pads"];

const INITIAL = { full_name: "", email: "", phone: "", company: "", portfolio_size: "", role: ROLES[0] };

const inputCls =
  "w-full rounded-md border border-slate-200 bg-[#EDF4FB] px-4 py-3 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60 transition-all";

export const CertForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/certification-interest", form);
      toast.success("You're on the list", {
        description: "We'll notify you when Community Operations Certification opens for enrollment.",
      });
      setDone(true);
    } catch (err) {
      toast.error("Could not join the waitlist", {
        description: formatApiError(err.response?.data?.detail),
      });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="technical-card p-7 sm:p-9 shadow-2xl text-center" data-testid="cert-form-success">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-moss/50 bg-moss/10 mb-5">
          <CheckCircle2 size={22} className="text-green-700" />
        </span>
        <p className="font-cabinet text-xl font-bold text-slate-900">Seat reserved, {form.full_name.split(" ")[0]}.</p>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
          You're on the cohort-one list. Grab the one-page curriculum to share with your team.
        </p>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/certification/curriculum.pdf`}
          data-testid="cert-curriculum-download"
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-cadmium px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover hover:-translate-y-0.5"
        >
          <Download size={16} /> Download the COC Curriculum (PDF)
        </a>
        <button
          data-testid="cert-form-add-another"
          onClick={() => { setForm(INITIAL); setDone(false); }}
          className="mt-4 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-sky-600 transition-colors"
        >
          Reserve another seat
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="cert-form" className="technical-card p-7 sm:p-9 shadow-2xl">
      <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-1">
        Certification Waitlist
      </p>
      <p className="text-sm text-muted-foreground mb-7">
        Cohort one opens to a limited number of portfolios. Reserve your operators' seats.
      </p>
      <div className="space-y-4">
        <input
          data-testid="cert-form-name-input"
          required
          value={form.full_name}
          onChange={set("full_name")}
          placeholder="Full name"
          className={inputCls}
        />
        <input
          data-testid="cert-form-email-input"
          required
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="Work email"
          className={inputCls}
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            data-testid="cert-form-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            placeholder="Phone (optional)"
            className={inputCls}
          />
          <select
            data-testid="cert-form-size-select"
            value={form.portfolio_size}
            onChange={set("portfolio_size")}
            className={inputCls}
          >
            <option value="">Portfolio size (optional)</option>
            {PORTFOLIO_SIZES.filter(Boolean).map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <input
          data-testid="cert-form-company-input"
          value={form.company}
          onChange={set("company")}
          placeholder="Company / portfolio (optional)"
          className={inputCls}
        />
        <select
          data-testid="cert-form-role-select"
          value={form.role}
          onChange={set("role")}
          className={inputCls}
        >
          {ROLES.map((r) => <option key={r}>{r}</option>)}
        </select>
      </div>
      <button
        type="submit"
        data-testid="cert-form-submit-button"
        disabled={loading}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-cadmium px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium-hover hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-60"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Medal size={16} />}
        {loading ? "Reserving…" : "Reserve My Seat"}
      </button>
      <p className="mt-4 text-center text-[11px] font-mono uppercase tracking-wider text-muted-foreground/60">
        Free to join · cohort one priority · curriculum PDF on signup
      </p>
    </form>
  );
};
